import { Box, Button, Card, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal, createDataCollectionContext, dbTimeToUserTime, useMobileContext, useValidatedSorting, type ConfirmModalHandle } from '@shared';
import { useMemo, useRef } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useAuth, useDrawer, UsersService } from '@admin';
import UserDrawer from './UserDrawer';
import AddUserDrawer from './AddUserDrawer';

const columnHelper = createColumnHelper<User>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function Users() {
	const { t } = useTranslation();
	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(() => createDataCollectionContext<User>(), []);
	const { user, isSuperAdmin, refetchUser } = useAuth();
	const { openDrawer } = useDrawer();
	const { isMobile } = useMobileContext();

	function getUserAccountRoleTranslation(name: string): string {
		switch (name) {
			case 'admin':
				return t('general.admin');
			case 'regular':
				return t('general.regular');
			default:
				return t('general.unknown_role');
		}
	}

	const columns = [
		columnHelper.accessor('firstName', {
			header: t('general.first_name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('lastName', {
			header: t('general.last_name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('email', {
			header: t('general.email'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('phone', {
			header: t('general.phone'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text userSelect={'text'}>{row.original.phone}</Text>;
			},
		}),
		columnHelper.accessor(row => row.userAccounts?.[0]?.userAccountRole ?? '', {
			id: 'userAccountRole',
			header: t('general.role'),
			enableSorting: false,
			cell: ({ row }) => {
				return <Text>{getUserAccountRoleTranslation(row.original.userAccounts?.[0]?.userAccountRole || '')}</Text>;
			},
		}),
		columnHelper.accessor('timeLatestLogin', {
			header: t('general.time_latest_login'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeLatestLogin) {
					return <Text>{t('general.never_logged_in')}</Text>;
				}
				return <Text>{dbTimeToUserTime(row.original.timeLatestLogin, TIMEZONE)}</Text>;
			},
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{dbTimeToUserTime(row.original.timeCreated, TIMEZONE)}</Text>;
			},
		}),
		columnHelper.accessor('timeModified', {
			header: t('general.time_modified'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeModified) {
					return null;
				}
				return <Text>{dbTimeToUserTime(row.original.timeModified, TIMEZONE)}</Text>;
			},
		}),
	];

	useValidatedSorting('users', columns);

	const confirmRemoveRef = useRef<ConfirmModalHandle>(null);

	const isAdmin = user?.userAccounts?.[0]?.userAccountRole === 'admin' || isSuperAdmin;

	return (
		<Card.Root flex={1} display="flex">
			<Card.Body display="flex" flexDirection="column" padding={5} gap={5} overflow={isMobile ? 'initial' : 'auto'}>
				<DataCollectionContextProvider
					dataKey="users"
					keyAttribute="userID"
					emptyDataText={t('general.no_users_found')}
					fetchFn={(...args) => UsersService.getAllResources(...args)}
					queryParams={{ scopeWith: 'timeLatestLogin' }}
					initialItemsPerPage={25}
					columns={columns}
					defaultHiddenColumns={[]}
					isServerSide={true}
					isSelectable={false}
					defaultSort={{ id: 'timeLatestLogin', desc: true }}
					onClickRow={row => {
						if (!user) {
							return;
						}
						openDrawer({
							id: 'userDrawer',
							content: <UserDrawer isSuperAdmin={isSuperAdmin} self={user} refetchUser={refetchUser} userID={row.original.userID} />,
						});
					}}
				>
					<Flex gap={2} flexWrap="wrap" flexShrink={0} ml="auto">
						<DataCollectionTableHeader />
						{isAdmin ? (
							<Button
								id="addUser"
								onClick={() => {
									openDrawer({
										id: 'addUserDrawer',
										content: <AddUserDrawer />,
									});
								}}
							>
								{t('general.add_user')}
							</Button>
						) : null}
					</Flex>
					<Box flex={1} overflow="auto" minH={0}>
						<DataCollectionTable />
					</Box>

					<Flex gap={2} flexShrink={0}>
						<DataCollectionTableFooter />
						<ConfirmModal ref={confirmRemoveRef} />
					</Flex>
				</DataCollectionContextProvider>
			</Card.Body>
		</Card.Root>
	);
}
